import type {
  UserMailbox,
  EmailProvider,
  EmailSyncStatus,
} from 'src/types/crm-mailbox';

import {showApiError} from 'src/action-creators/modal';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {thunkify as flow} from 'src/utils/thunks';
import * as reduxApiBff from 'src/utils/redux-api-bff';


const BASE_API_PATH = 'mailbox';
export const initialEmailSyncStatus: EmailSyncStatus = {
  userEmailId: 0,
  email: '',
  connectivityStatus: '',
  isSignatureCreated: false,
  areUsersInvited: false,
  isMileStoneSet: false,
  syncDetails: {
    pollMilliSeconds: 0,
    syncStatus: '',
    percentage: 0,
  },
};

export const USER_MAILBOX = 'crm-mailbox/user-emails';
export const EMAIL_PROVIDERS = 'crm-mailbox/email-providers';
export const EMAIL_SYNC_START = 'crm-mailbox/email-sync-start';
export const EMAIL_SYNC_ERROR = 'crm-mailbox/email-sync-error';

type UserMailboxAction = {
  type: typeof USER_MAILBOX;
  payload: UserMailbox[] | null | undefined;
};

type EmailProvidersAction = {
  type: typeof EMAIL_PROVIDERS;
  payload: EmailProvider[] | null | undefined;
};

type EmailSyncStartAction = {
  type: typeof EMAIL_SYNC_START;
  payload: EmailSyncStatus | null | undefined;
};

type EmailSyncErrorAction = {
  type: typeof EMAIL_SYNC_ERROR;
  payload: string | null | undefined;
};

export type CrmMailboxActions =
  | UserMailboxAction
  | EmailProvidersAction
  | EmailSyncStartAction
  | EmailSyncErrorAction;

const getUserMailboxes = (payload: UserMailbox[]): UserMailboxAction => ({
  type: USER_MAILBOX,
  payload,
});

const receiveEmailProviders = (
  payload: EmailProvider[],
): EmailProvidersAction => ({
  type: EMAIL_PROVIDERS,
  payload,
});

export const receiveEmailSyncStart = (
  payload?: EmailSyncStatus | null,
): EmailSyncStartAction => ({
  type: EMAIL_SYNC_START,
  payload,
});

const receiveEmailSyncError = (payload: string): EmailSyncErrorAction => ({
  type: EMAIL_SYNC_ERROR,
  payload,
});

export const fetchUserMailboxes: () => ThunkAction<void> = flow()(
  () => (dispatch: Dispatch) => {
    return dispatch(reduxApiBff.get(`${BASE_API_PATH}/user-emails`)).then(
      (response: {userEmails: UserMailbox[]}) => {
        dispatch(getUserMailboxes(response.userEmails));
      },
      (error) => {
        dispatch(showApiError(error, error?.responseBody?.message));
      },
    );
  },
);

export const getEmailProviders: () => ThunkAction<void> = flow()(
  () => (dispatch: Dispatch) => {
    return dispatch(reduxApiBff.get(`${BASE_API_PATH}/providers`)).then(
      ({providers}: {providers: EmailProvider[]}) => {
        dispatch(receiveEmailProviders(providers));
      },
      (error) => {
        dispatch(showApiError(error, error?.responseBody?.message));
      },
    );
  },
);

// @ts-expect-error - TS2322 - Type '(payload: { mailProvider: string; noOfDaysSyncRequestedFor: number; }) => (dispatch: Dispatch) => Promise<void | { redirectUrl: string; userEmailId: number; }>' is not assignable to type '(payload: { mailProvider: string; noOfDaysSyncRequestedFor: number; }) => ThunkAction<{ redirectUrl: string; userEmailId: number; } | null | undefined>'.
export const startEmailSync: (payload: {
  mailProvider: string;
  noOfDaysSyncRequestedFor: number;
}) => ThunkAction<
  | {
      redirectUrl: string;
      userEmailId: number;
    }
  | null
  | undefined
> = flow()((payload) => (dispatch: Dispatch) => {
  return dispatch(reduxApiBff.post(`${BASE_API_PATH}/sync`, payload)).then(
    (response: {redirectUrl: string; userEmailId: number}) => {
      return response;
    },

    (error) => {
      dispatch(showApiError(error, error?.responseBody?.message));
    },
  );
});

let syncPollingIntervalId: number | null | undefined = null;

export const startEmailSyncPolling: (
  userEmailId: number,
  // @ts-expect-error - TS2322 - Type '(dispatch: Dispatch) => void' is not assignable to type 'ThunkAction<void>'.
) => ThunkAction<void> = (userEmailId) => (dispatch: Dispatch) => {
  const INITIAL_POLL_INTERVAL = 5000;
  const CONNECTIVITY_STATUS = {
    ACTIVE: 'CONNECTIVE_ACTIVE',
  } as const;

  let pollMilliSeconds = INITIAL_POLL_INTERVAL;
  // @ts-expect-error - TS7034 - Variable 'intervalId' implicitly has type 'any' in some locations where its type cannot be determined.
  let intervalId = null;

  const clearPollingInterval = () => {
    // @ts-expect-error - TS7005 - Variable 'intervalId' implicitly has an 'any' type.
    if (intervalId !== null) {
      // @ts-expect-error - TS7005 - Variable 'intervalId' implicitly has an 'any' type.
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const startPolling = (interval: number) => {
    clearPollingInterval();
    intervalId = setInterval(pollSyncStatus, interval);
  };

  const pollSyncStatus = () => {
    return dispatch(
      reduxApiBff.post(`${BASE_API_PATH}/sync-status`, {userEmailId}),
    )
      .then((response: EmailSyncStatus) => {
        dispatch(receiveEmailSyncStart(response));
        return response;
      })
      .then((response) => {
        pollMilliSeconds = response.syncDetails.pollMilliSeconds;

        if (
          response.email &&
          response.connectivityStatus === CONNECTIVITY_STATUS.ACTIVE
        ) {
          clearPollingInterval();
        } else {
          startPolling(pollMilliSeconds);
        }
      })
      .catch((error) => {
        dispatch(receiveEmailSyncError(error?.responseBody?.message));
        dispatch(showApiError(error, error?.responseBody?.message));
      });
  };

  // Initially set the polling interval if it's not already running
  if (syncPollingIntervalId === null) {
    // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
    syncPollingIntervalId = setInterval(pollSyncStatus, pollMilliSeconds);
  }
};

// @ts-expect-error - TS2322 - Type '() => (dispatch: Dispatch) => Promise<void | { url: string; }>' is not assignable to type '() => ThunkAction<{ url: string; } | null | undefined>'.
export const getLearMoreURL: () => ThunkAction<
  | {
      url: string;
    }
  | null
  | undefined
> = flow()(() => (dispatch: Dispatch) => {
  return dispatch(reduxApiBff.get(`${BASE_API_PATH}/learn-more`)).then(
    (response: {url: string}) => {
      return response;
    },
    (error) => {
      dispatch(showApiError(error, error?.responseBody?.message));
    },
  );
});
